import React, { useEffect, useRef, useState } from 'react';
import { formatDuration, youtubeVideoId } from '../learning/learningModel';
import { YouTubeThumbnail } from '../learning/LearningCenter';
import { loadYouTubeApi } from '../learning/youtubePlayerApi';

export default function YouTubeAdminPreview({ url, customThumb, onDuration }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const onDurationRef = useRef(onDuration);
  const [status, setStatus] = useState('loading');
  const [duration, setDuration] = useState(null);
  const videoId = youtubeVideoId(url);
  onDurationRef.current = onDuration;

  useEffect(() => {
    if (!videoId) return undefined;
    let active = true;
    let timer;
    let attempts = 0;
    setStatus('loading');
    setDuration(null);

    const readDuration = (player) => {
      if (!active) return;
      const seconds = Math.round(Number(player.getDuration?.()) || 0);
      if (seconds > 0) {
        if (timer) window.clearInterval(timer);
        setDuration(seconds);
        setStatus('ready');
        onDurationRef.current?.(seconds);
      } else if (attempts >= 20) {
        if (timer) window.clearInterval(timer);
        setStatus('manual');
      }
      attempts += 1;
    };

    loadYouTubeApi().then((YT) => {
      if (!active || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId,
        playerVars: { controls: 1, playsinline: 1, rel: 0 },
        events: {
          onReady: (event) => {
            readDuration(event.target);
            if (!duration) timer = window.setInterval(() => readDuration(event.target), 500);
          },
          onStateChange: (event) => readDuration(event.target),
          onError: () => active && setStatus('error'),
        },
      });
    }).catch(() => active && setStatus('error'));

    return () => {
      active = false;
      if (timer) window.clearInterval(timer);
      playerRef.current?.destroy?.();
      playerRef.current = null;
    };
  }, [videoId]);

  if (!videoId) return null;
  const chapter = { id: videoId, video_source: 'youtube', video_url: url, thumb_url: customThumb };
  return (
    <div className="sml-adl-youtube-meta" data-testid="youtube-admin-preview">
      <div className="sml-adl-preview" ref={hostRef} />
      <div className="sml-adl-auto-thumb">
        <YouTubeThumbnail chapter={chapter} alt="Miniatura del capítulo"
          fallback={<span className="sml-adl-thumb-neutral">Sin miniatura disponible</span>} />
        <span>{customThumb ? 'Miniatura personalizada' : 'Miniatura automática de YouTube'}</span>
      </div>
      <small role="status">
        {status === 'loading' && 'Obteniendo duración real…'}
        {status === 'ready' && `Duración detectada: ${formatDuration(duration)}`}
        {(status === 'manual' || status === 'error') && 'No se pudo obtener la duración. Podés ingresarla manualmente.'}
      </small>
    </div>
  );
}
